import React, { useEffect, useState, useContext, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import { Box, Button, CircularProgress, Container, FormControl, FormHelperText, Input, InputLabel, Paper, Typography } from '@mui/material';
import AuthContext from '../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconShadowUrl from 'leaflet/dist/images/marker-shadow.png';
import GoogleAddressSearch from '../components/GoogleAddressSearch';

const DefaultIcon = L.icon({
  iconUrl,
  shadowUrl: iconShadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

function ClickHandler({ onMapClick }) {
  useMapEvents({
    click(e) {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function FlyTo({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.flyTo(position, 16);
  }, [position, map]);
  return null;
}

const StudentPOIsPage = () => {
  const { authTokens, logoutUser, backendUrl, profile } = useContext(AuthContext);
  const params = useParams();
  const navigate = useNavigate();

  const [address, setAddress] = useState('');
  const [addressId] = useState(null);
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [markerPos, setMarkerPos] = useState(null);
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState('');

  const handleMapClick = useCallback((lat, lng) => {
    setLatitude(lat);
    setLongitude(lng);
    setMarkerPos([lat, lng]);
  }, []);

  const handleSearchResult = useCallback(({ address: addr, lat, lng }) => {
    setAddress(addr);
    setLatitude(lat);
    setLongitude(lng);
    setMarkerPos([lat, lng]);
  }, []);

  const getStudent = useCallback(async () => {
    const response = await fetch(backendUrl + 'api/student/' + params['student_id'], {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + String(authTokens.access),
      },
    });
    const data = await response.json();
    setStudent(data);
    setLoading(false);
    if (response.statusText === 'Unauthorized') {
      logoutUser();
    }
  }, []);

  useEffect(() => {
    getStudent();
  }, []);

  const addAddress = async () => {
    const response = await fetch(backendUrl + 'api/save-student-poi/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + String(authTokens.access),
      },
      body: JSON.stringify({
        id: addressId,
        address,
        name: comments,
        studentId: student.child_id,
        longitude,
        latitude,
      }),
    }).catch(console.error);

    if (response?.status === 200) {
      navigate('/student/' + student.child_id);
    } else if (response?.statusText === 'Unauthorized') {
      logoutUser();
    }
  };

  if (!profile) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  const center = [profile.schoollatitude, profile.schoollongitude];

  return (
    <Container sx={{ m: 10 }}>
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="30vh">
          <CircularProgress />
        </Box>
      ) : (
        <Typography variant="h5" sx={{ m: 4 }}>
          {student.firstname} {student.lastname}
        </Typography>
      )}

      <Paper elevation={3}>
        <Box sx={{ p: 2 }}>
          <GoogleAddressSearch
            value={address}
            onChange={(val) => setAddress(val)}
            onSelect={handleSearchResult}
            label="Search for an address…"
          />
        </Box>
        <MapContainer
          center={center}
          zoom={14}
          style={{ height: 400, width: '100%', cursor: 'crosshair' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ClickHandler onMapClick={handleMapClick} />
          <FlyTo position={markerPos} />
          {markerPos && <Marker position={markerPos} />}
        </MapContainer>

        <Box sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <FormControl sx={{ width: 300 }}>
            <InputLabel htmlFor="address">Address</InputLabel>
            <Input
              id="address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              aria-describedby="address"
            />
            <FormHelperText>Filled automatically when searching or click the map</FormHelperText>
          </FormControl>

          <FormControl sx={{ width: 300 }}>
            <InputLabel htmlFor="comments">Comments</InputLabel>
            <Input
              id="comments"
              onChange={(e) => setComments(e.target.value)}
              value={comments}
            />
            <FormHelperText />
          </FormControl>

          <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={addAddress}
              disabled={!latitude || !address}
            >
              Add address
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default StudentPOIsPage;
